import { Koi } from '@ksyos/koi';
import { FinanceSearchField } from '@common/enums/financeSearchField';
import { NotFundedReason } from '@common/enums/notFundedReason';
import { csProcessIdRegex } from '@common/utils/regularExpressions';
import type { BatchRunValidated } from './batchRun';
import type { ISchemaContainer } from './schemaContainer';
import { ExportStatusFilter } from '../enums/exportStatusFilter';
import { FundingErrorCode } from '../enums/fundingErrorCode';
import { ServiceType } from '../enums/serviceType';
import { TreatedStatusFilter } from '../enums/treatedStatusFilter';
import { batchRunSchema } from './batchRun';
import { financingSearchFilterTextSchema } from './financingSearchFilterText';
import { pagingParametersValidator } from './pagingParametersValidator';

type FinancingFilterBase = {
    batchRun?: BatchRunValidated;
    endDate?: Date;
    episodeCategoryId?: string;
    exportStatus?: ExportStatusFilter;
    fundingError?: FundingErrorCode;
    fundingErrorInformation?: string;
    identifierSocialSecurity?: string;
    insurerId?: string;
    noInvoiceNorDeclaration?: boolean;
    notFundedReason?: NotFundedReason;
    onlyDeclarations?: boolean;
    onlyInvoices?: boolean;
    serviceId?: string;
    serviceType?: ServiceType;
    startDate?: Date;
    treatedStatus?: TreatedStatusFilter;
};

const financingFilterBaseSchemaArgs = {
    batchRun: batchRunSchema.optional(),
    endDate: Koi.when('startDate', {
        is: Koi.exist(),
        otherwise: Koi.koi().date(),
        then: Koi.koi().date().endDate(),
    }).allow(null),
    episodeCategoryId: Koi.string().uuid(),
    exportStatus: Koi.koi().enum(ExportStatusFilter),
    fundingError: Koi.koi().enum(FundingErrorCode),
    fundingErrorInformation: Koi.string().max(25),
    identifierSocialSecurity: Koi.string().elfproef().allow(''),
    insurerId: Koi.string().uuid(),
    noInvoiceNorDeclaration: Koi.boolean(),
    notFundedReason: Koi.koi().enum(NotFundedReason),
    onlyDeclarations: Koi.boolean(),
    onlyInvoices: Koi.boolean(),
    serviceId: Koi.string().uuid(),
    serviceType: Koi.koi().enum(ServiceType),
    startDate: Koi.koi().date().allow(null),
    treatedStatus: Koi.koi().enum(TreatedStatusFilter),
};

type FinancingSearchInputFilter = FinancingFilterBase & { searchField?: FinanceSearchField; searchText?: string };

export const financingSearchInputFilterValidationSchema: ISchemaContainer<FinancingSearchInputFilter> = {
    schema: Koi.object({
        ...financingFilterBaseSchemaArgs,
        searchField: Koi.koi().enum(FinanceSearchField),
        searchText: financingSearchFilterTextSchema.optional(),
    }).options({ convert: false, presence: 'optional' }),
};

export type IFinancingFilter = FinancingFilterBase & {
    groupName?: string;
    organizationName?: string;
    paging?: {
        limit: number;
        offset: number;
    };
    processId?: string;
    relationNumber?: string;
    requesterPractitionerUid?: string;
    searchText?: string;
};

export const financingFilterValidation = Koi.object({
    ...financingFilterBaseSchemaArgs,
    groupName: Koi.string().max(255),
    organizationName: Koi.string().max(255),
    paging: pagingParametersValidator.optional(),
    processId: Koi.string().regex(csProcessIdRegex).max(100000),
    relationNumber: Koi.string().max(255),
    requesterPractitionerUid: Koi.string().max(255),
    searchText: Koi.string(),
});

export const financingFilterValidationSchema: ISchemaContainer<IFinancingFilter> = {
    schema: financingFilterValidation.options({ convert: false, presence: 'optional' }),
};
